import { TextField } from "@mui/material";
import { NewCommentButton, NewCommentInputContainer } from "./alertSummary.styled";
import { useContext, useEffect, useState, useRef } from "react";
import CommentDetail, { CommentDetailProps } from "./commentDetail";
import { UserClaims, AuthContext } from "core/contexts/auth/auth";
import { getAlertComments, postUserComment } from "modules/ac2/services/alertService";
import { convertUnixTimeToDate } from  "core/utils/dateTimeUtils";
import {  v4 as uuidv4 } from 'uuid';


export interface AlertCommentsProps {
    t: any;
    theme: any;
    user: UserClaims | null;
    alertId: string;
    initialComments: CommentDetailProps[];
}

export default function AlertComments({t, theme, user, alertId, initialComments}: AlertCommentsProps) {
    const [ currentComments, setComments ] = useState(initialComments);
    const { checkAuthToken } = useContext(AuthContext)
    const [ commentText, setCommentText ] = useState('');

    /**
    * Fetch new currentComments every X seconds and replace the current list
     * @param None 
    */
    useEffect(() => {
        setComments(initialComments)
        const fetchComments = async () => {
            const token = await checkAuthToken();
            const config = { 'Authorization': token || '', 'Content-Type': 'application/json' };
            const response = await getAlertComments(config, alertId);
            if (response) {
                setComments(() => ([
                    ...response
                ]))
            }
        };
    
        const intervalId = setInterval(fetchComments, 30000);
        return () => clearInterval(intervalId);

        // This will never trigger more than once after it has been filled
    }, [initialComments])

    /**
     * Manage the currentComments, the summary modal will periodically poll for new currentComments from other users
     * @param currentComments 
     */
    const addComment = (commentText, timeNow) => {
        setComments((current: any) => ([
            { 
                commentId: uuidv4(),
                firstName: user?.given_name || '',
                lastName: user?.family_name || '',
                userEmail: user?.email || '',
                postedOn: convertUnixTimeToDate(timeNow),
                commentText: commentText,
                image: user?.image || ''
            },
            ...current
        ]))
    };

    /**
     * Add the comment to the backend
     */
    async function handleSubmitComment() {
        const token = await checkAuthToken();
        const config = { 'Authorization': token || '', 'Content-Type': 'application/json' };
        const timeNow = Date.now();

        const response = await postUserComment(
            config,
            [{
                userEmail: user?.email,
                postedOn: timeNow,
                commentText: commentText
            }],
            alertId
        );

        if (response) {
            setCommentText('');
            addComment(commentText, timeNow);
        }
    }

    return(
        <>
            <strong className="pb-2" style={{fontSize: theme.font.large}}>{t("INCIDENTS_PAGE.COMMENTS")}:</strong>
            <NewCommentInputContainer>
                <div className="input-wrapper" style={{ width: '100%', position: 'relative' }}>
                    <TextField  
                        id="outlined-textarea"
                        placeholder={t("INCIDENTS_PAGE.ADD_COMMENT") || "Add a comment"}
                        multiline
                        value={commentText}
                        onChange={(event) => setCommentText(event.target.value)}
                        className="w-100"
                        style={{fontSize: theme.font.medium}}
                        InputProps={{
                            rows: 3,
                            multiline: true,
                            inputComponent: 'textarea'
                        }}
                    />
                </div>
                <NewCommentButton variant="outlined" onClick={handleSubmitComment} disabled={commentText === "" ? true : false}>
                    <p>{t("INCIDENTS_PAGE.SUBMIT_COMMENT")}</p>
                </NewCommentButton>
            </NewCommentInputContainer>

            {currentComments.map((comment, index) => {
                if (comment.firstName) {
                    return (
                        <CommentDetail
                            key={`${comment.commentId}`}
                            initialsColor={'#006666'}
                            firstName={comment.firstName}
                            lastName={comment.lastName}
                            userEmail={comment.userEmail}
                            postedOn={comment.postedOn}
                            commentText={comment.commentText}
                        />
                    );
                } else {
                    return(<></>)
                }
            })}
        </>
    )
}